import { useTranslation } from 'react-i18next';

import { LanguageSwitcher } from '../../shared/LanguageSwitcher';
import { ButtonLink } from '../../shared/ui';

/**
 * P2. The public 404 for paths that belong to nothing - a typo in the address
 * bar, a stale link from outside, an old `/dashboard/:key` bookmark that was
 * never a real screen. THE GATE DOES NOT GET THIS PATH: the `/*` splat that
 * used to send every unknown URL at `/login` is now scoped to `/dashboard/*`,
 * so a signed-out student typing garbage never sees the "your session ended"
 * screen that made a typo feel like an outage.
 *
 * DESIGN.md §Layout: one column at 390px, the primary action full-width in the
 * bottom third of the card (The Thumb Zone Rule). "Back to home" is the ship
 * home an 18-year-old actually needs; "Sign in" is the second thought.
 */
export function NotFoundPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-surface pb-20">
      <header className="bg-surface-container-lowest border-b border-surface-container">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <ButtonLink to="/" variant="ghost" className="px-2 min-h-[44px]">
            <span aria-hidden="true" className="material-symbols-outlined text-[22px]">
              arrow_back
            </span>
            {t('legal.backHome')}
          </ButtonLink>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-12">
        <div className="bg-surface-container-lowest rounded-3xl px-6 py-10 flex flex-col items-center text-center shadow-soft">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-surface-container-high mb-6 shadow-sm">
            <span className="material-symbols-outlined text-4xl text-primary">search_off</span>
          </div>
          <p className="font-headline-xl text-headline-xl text-primary tracking-tight">404</p>
          <h1 className="font-headline-lg text-headline-lg text-on-surface mt-2">
            {t('notFound.title')}
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-3 max-w-sm">
            {t('notFound.message')}
          </p>

          <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-3 mt-8">
            <ButtonLink to="/" className="sm:flex-initial">
              <span aria-hidden="true" className="material-symbols-outlined text-[20px]">
                home
              </span>
              {t('notFound.home')}
            </ButtonLink>
            <ButtonLink to="/login" variant="outline">
              {t('notFound.signIn')}
            </ButtonLink>
          </div>
        </div>
      </main>
    </div>
  );
}
